/**
 * Browser-oriented mock of Node's fs that loads files via fetch/HTTP.
 * This enables running server-ish code in the browser/test environment.
 *
 * Types mirror `node:fs`. The imports below are type-only, so they are erased
 * at bundle time and never pull the real (unavailable) module into the browser.
 */
import type {
  PathLike,
  PathOrFileDescriptor,
  ObjectEncodingOptions,
  MakeDirectoryOptions,
  WriteFileOptions,
  RmOptions,
  NoParamCallback,
  Mode,
} from 'node:fs';

export interface FSStats {
  isDirectory: () => boolean;
  isSymbolicLink: () => boolean;
  isFile: () => boolean;
}

type ReadFileResult = string | Buffer | Uint8Array;
/** Options accepted by `readFile`/`readFileSync` (matches Node's encoding/flag union). */
type ReadFileOptions = (ObjectEncodingOptions & { flag?: string }) | BufferEncoding | null;
/** Like Node's readFile callback, but `data` is absent on the error path. */
type ReadFileCallback = (err: NodeJS.ErrnoException | null, data?: ReadFileResult) => void;

const getBase = (): string =>
  typeof window !== 'undefined'
    ? (window as Window & { __GOTTI_FS_BASE?: string }).__GOTTI_FS_BASE || '/'
    : '/';

const normalize = (p: PathOrFileDescriptor): string => {
  // Coerce PathLike (string | Buffer | URL) or a numeric fd to a string path.
  const raw = String(p ?? '');
  if (!raw) return getBase();
  // If already absolute URL
  if (/^https?:\/\//i.test(raw)) return raw;
  // Strip leading ./ or repeated slashes
  const clean = raw.replace(/^(\.\/|\/)+/, '');
  const base = getBase();
  const sep = base.endsWith('/') ? '' : '/';
  // Ensure we fetch from same-origin path
  return `${base}${sep}${clean}`.replace(/\/+/g, '/');
};

const toBuffer = (ab: ArrayBuffer): Buffer | Uint8Array => {
  const BufferCtor = (globalThis as { Buffer?: typeof Buffer }).Buffer;
  return BufferCtor ? BufferCtor.from(ab) : new Uint8Array(ab);
};

// Async readFile via fetch
export const readFile = (
  path: PathOrFileDescriptor,
  options?: ReadFileOptions | ReadFileCallback,
  callback?: ReadFileCallback,
): void => {
  let cb = callback;
  let enc: BufferEncoding | null | undefined;
  if (typeof options === 'function') cb = options;
  else if (typeof options === 'string') enc = options;
  else if (options && typeof options === 'object') enc = options.encoding;

  const url = normalize(path);
  fetch(url)
    .then(async (res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      if (enc && enc.toLowerCase() === 'utf8') return res.text();
      const ab = await res.arrayBuffer();
      return toBuffer(ab);
    })
    .then((data) => cb && cb(null, data))
    .catch((err) => cb && cb(err));
};

// Minimal sync XHR fallback for readFileSync (text only)
export const readFileSync = (path: PathOrFileDescriptor, options?: ReadFileOptions): ReadFileResult => {
  const enc = typeof options === 'string' ? options : options?.encoding;
  const url = normalize(path);
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, false);
    xhr.send();
    if (xhr.status < 200 || xhr.status >= 300) throw new Error(`HTTP ${xhr.status}`);
    if (enc && enc.toLowerCase() === 'utf8') return xhr.responseText ?? '';
    // Binary fallback best-effort
    const text = xhr.responseText ?? '';
    const buf = (typeof TextEncoder !== 'undefined') ? new TextEncoder().encode(text) : new Uint8Array([]);
    return toBuffer(buf.buffer);
  } catch {
    return enc && enc.toLowerCase() === 'utf8' ? '' : toBuffer(new ArrayBuffer(0));
  }
};

export const existsSync = (path: PathLike): boolean => {
  const url = normalize(path);
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('HEAD', url, false);
    xhr.send();
    return xhr.status >= 200 && xhr.status < 400;
  } catch {
    return false;
  }
};

export const statSync = (_path: PathLike): FSStats => {
  return {
    isDirectory: () => false,
    isSymbolicLink: () => false,
    isFile: () => true,
  };
};

export const lstatSync = statSync;

export const readdirSync = (_dir: PathLike, _options?: ObjectEncodingOptions | BufferEncoding | null): string[] => [];
export const mkdirSync = (_path: PathLike, _options?: Mode | MakeDirectoryOptions | null): void => {};
export const writeFileSync = (_path: PathOrFileDescriptor, _data: string | NodeJS.ArrayBufferView, _options?: WriteFileOptions): void => {};
// No-ops in the browser: any server-ish module bundled for the browser (e.g.
// sandbox-ai-v2's llm.ts deep-recording hook) imports these, but their real
// effects (file writes) are gated by env/flags that are never set in-browser.
// Without these named exports the module fails to load and crashes the app.
export const appendFileSync = (_path: PathOrFileDescriptor, _data: string | Uint8Array, _options?: WriteFileOptions): void => {};
export const rmSync = (_path: PathLike, _options?: RmOptions): void => {};
// PubSub.close() unlinks its zmq ipc socket FILE (server-side teardown). In the
// browser there is no zmq, no ipc bind and no file — but @gotti/server is
// bundled into the browser harness, so the import must resolve or the whole
// app fails to load (this exact class of break took CI down on 2026-08-07).
export const unlinkSync = (_path: PathLike): void => {};
// Cluster trust (packages/server/src/trust) writes key files and tightens the ipc
// socket directory — server-only paths, but the imports must resolve in the browser
// bundle. Opening a file here is a bug, so it throws instead of faking a descriptor.
export const chmodSync = (_path: PathLike, _mode: Mode): void => {};
export const openSync = (path: PathLike, _flags?: string | number, _mode?: Mode | null): number => {
  throw new Error(`fs.openSync(${String(path)}) is not available in the browser`);
};
export const closeSync = (_fd: number): void => {};
export const writeFile = (
  _path: PathOrFileDescriptor,
  _data: string | NodeJS.ArrayBufferView,
  options: WriteFileOptions | NoParamCallback,
  callback?: NoParamCallback,
): void => {
  const cb = typeof options === 'function' ? options : callback;
  setTimeout(() => cb && cb(null), 0);
};

export const writeSync = (
  _fd: number,
  buffer: string | NodeJS.ArrayBufferView,
  _offset?: number | null,
  _length?: number | null,
  _position?: number | null,
): number => (typeof buffer === 'string' ? buffer.length : buffer.byteLength);

export const constants = {
  O_RDONLY: 0,
  O_RDWR: 2,
  O_CREAT: 0x40,
  O_TRUNC: 0x200,
  O_APPEND: 0x400,
  O_EXCL: 0x80,
  F_OK: 0,
  R_OK: 4,
  W_OK: 2,
  X_OK: 1,
} as const;

// Promises API subset
export const promises = {
  readFile: (path: PathOrFileDescriptor, options?: ReadFileOptions): Promise<ReadFileResult> =>
    new Promise((resolve, reject) =>
      readFile(path, options, (e, d) => (e ? reject(e) : resolve(d as ReadFileResult)))),
};

export default {
  readFileSync,
  existsSync,
  readFile,
  statSync,
  lstatSync,
  readdirSync,
  mkdirSync,
  writeFileSync,
  appendFileSync,
  rmSync,
  unlinkSync,
  chmodSync,
  openSync,
  closeSync,
  writeFile,
  writeSync,
  constants,
  promises,
};
